import { useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const DEFAULT_HERO = "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/8567d59b9e7241108006e591ab2044bfd8e6dc40be27e280428942c64ca392be.jpeg";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, FileText, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";


export default function Kontakt() {
  const { t, footer } = useContent();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Wiadomość została wysłana. Dziękujemy!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const contacts = [
    { icon: MapPin, label: "Adres siedziby", val: footer.address },
    { icon: Phone, label: "Telefon", val: footer.phone, href: `tel:${(footer.phone || "").replace(/\s/g, "")}` },
    { icon: Mail, label: "E-mail", val: footer.email, href: `mailto:${footer.email}` },
    { icon: FileText, label: "KRS", val: footer.krs },
    { icon: Clock, label: "Godziny pracy biura", val: footer.hours },
  ];

  return (
    <div>
      <PageHeader label="Skontaktuj się" title="Kontakt i siedziba Izby" subtitle={t("kontakt_intro")} bgImage={t("kontakt_hero_image") ? fileUrl(t("kontakt_hero_image")) : DEFAULT_HERO} />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Dane rejestrowe</h2>
          <div className="mt-6 space-y-4">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-start gap-4 rounded-xl border border-border bg-card p-4" data-testid={`kontakt-${i}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ztk-blue/10 text-ztk-blue">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="label-mono text-[10px] text-muted-foreground">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="font-medium text-ztk-navy transition-colors hover:text-ztk-blue dark:text-white">{c.val}</a>
                  ) : (
                    <p className="font-medium text-ztk-navy dark:text-white">{c.val}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl border border-border">
            <iframe
              title="Mapa - ul. Traugutta 25, Łódź"
              width="100%"
              height="300"
              loading="lazy"
              style={{ border: 0 }}
              src="https://www.openstreetmap.org/export/embed.html?bbox=19.455%2C51.760%2C19.475%2C51.772&layer=mapnik&marker=51.766%2C19.465"
              data-testid="kontakt-map"
            />
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Napisz do nas</h2>
          <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="kontakt-form">
            <div>
              <Label htmlFor="c-name">Imię i nazwisko</Label>
              <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kontakt-name" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-email">E-mail</Label>
              <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="kontakt-email" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-subject">Temat</Label>
              <Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="kontakt-subject" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-message">Wiadomość</Label>
              <Textarea id="c-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="kontakt-message" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-ztk-blue hover:bg-ztk-navy800" data-testid="kontakt-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Wyślij wiadomość
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
